import { memo, useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Avatar } from "antd";
import {
  CloseOutlined,
  MinusOutlined,
  ExpandAltOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import _get from "lodash/get";
import DefaultLogo from "assets/images/logovietgrp.svg";
import UserStatusIndicator from "components/UserStatusIndicator";
import {
  closeChatWindow,
  minimizeChatWindow,
  popupSeenConversationRequest,
  closeList,
} from "providers/ChatPopupProvider/slice";
import ChatPopupMessageList from "./ChatPopupMessageList";
import ChatPopupMessageInput from "./ChatPopupMessageInput";

const ChatPopupWindow = ({ conversationId, minimized, index }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get conversation detail from popup state or conversation list
  const conversationDetail = useSelector(
    (state) =>
      state.chatPopup.windowConversationDetails[conversationId] ||
      state.chatPopup.conversations.entities[conversationId]
  );

  const user = _get(conversationDetail, "users[0]", {});
  const avatar = _get(user, "userDetail.avatar") || DefaultLogo;
  const online = _get(user, "online", 0);
  const lastSeen = _get(user, "lastSeen", null);
  const userName = user.name || "...";

  // Mark as seen when window opens (not minimized)
  useEffect(() => {
    if (!minimized && conversationId) {
      dispatch(popupSeenConversationRequest({ conversationId }));
    }
  }, [dispatch, conversationId, minimized]);

  // Fetch conversation detail if not available
  useEffect(() => {
    if (conversationId && !conversationDetail) {
      dispatch({
        type: "chatPopup/fetchConversationDetail",
        payload: { conversationId },
      });
    }
  }, [dispatch, conversationId, conversationDetail]);

  const handleClose = useCallback(() => {
    dispatch(closeChatWindow({ conversationId }));
  }, [dispatch, conversationId]);

  const handleMinimize = useCallback(() => {
    dispatch(minimizeChatWindow({ conversationId }));
  }, [dispatch, conversationId]);

  const handleExpand = useCallback(() => {
    dispatch(closeChatWindow({ conversationId }));
    dispatch(closeList());
    navigate(`/fonehouse/messengers/${conversationId}`);
  }, [dispatch, navigate, conversationId]);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Position: stack from right (desktop only)
  const rightOffset = isMobile ? undefined : 90 + index * 340;

  return (
    <div
      className={`chat-popup-window ${minimized ? "minimized" : ""}`}
      style={rightOffset != null ? { right: rightOffset } : undefined}
    >
      <div className="chat-popup-window-header" onClick={minimized ? handleMinimize : undefined}>
        <div className="header-user">
          {isMobile && !minimized && (
            <ArrowLeftOutlined className="action-icon" onClick={handleClose} />
          )}
          <div className="header-avatar">
            <Avatar size={28} src={avatar} />
            <UserStatusIndicator online={online} lastSeen={lastSeen} size="xs" />
          </div>
          <span className="header-name">{userName}</span>
        </div>
        <div className="header-actions">
          {!isMobile && (
            <ExpandAltOutlined className="action-icon" onClick={handleExpand} title="Mở trang đầy đủ" />
          )}
          <MinusOutlined className="action-icon" onClick={handleMinimize} />
          <CloseOutlined className="action-icon" onClick={handleClose} />
        </div>
      </div>
      {!minimized && (
        <div className="chat-popup-window-body">
          <ChatPopupMessageList conversationId={conversationId} />
          <ChatPopupMessageInput conversationId={conversationId} />
        </div>
      )}
    </div>
  );
};

export default memo(ChatPopupWindow);
